import React, { useState } from "react";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import { Popover, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EventIcon from "@material-ui/icons/Event";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { DATE_PRESETS, presetRange } from "./ledgerConstants";

const pad = (n) => String(n).padStart(2, "0");
const toStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toDate = (s) => (s ? new Date(`${s}T00:00:00`) : null);
const nice = (s) =>
  s ? toDate(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "Any";

const useStyles = makeStyles({
  trigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 40,
    padding: "0 10px 0 12px",
    border: "1px solid rgba(0,0,0,.23)",
    borderRadius: 4,
    background: "#fff",
    cursor: "pointer",
    fontSize: "0.85rem",
    color: "#0f172a",
    whiteSpace: "nowrap",
    "&:hover": { borderColor: "#f97316" },
  },
  paper: { width: 340, maxWidth: "calc(100vw - 24px)", padding: 12 },
  presets: { display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 },
  chip: {
    padding: "3px 10px",
    borderRadius: 9999,
    fontSize: "0.72rem",
    fontWeight: 600,
    border: "1px solid #e2e8f0",
    cursor: "pointer",
    color: "#475569",
    "&:hover": { borderColor: "#f97316" },
  },
  chipOn: { background: "#fff7ed", borderColor: "#f97316", color: "#c2410c" },
  seg: { display: "flex", gap: 6, marginBottom: 4 },
  segBtn: {
    flex: 1,
    textAlign: "left",
    padding: "6px 10px",
    borderRadius: 8,
    border: "1.5px solid #e2e8f0",
    cursor: "pointer",
    background: "#fff",
  },
  segOn: { borderColor: "#f97316", background: "#fffaf5" },
  segLabel: { fontSize: "0.6rem", fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", color: "#94a3b8" },
  segVal: { fontSize: "0.85rem", fontWeight: 700, color: "#0f172a" },
  footer: { display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 6 },
});

// Compact range picker: one trigger button -> popover with quick presets and
// a real calendar (From / To). Values are yyyy-mm-dd strings ("" = open).
export default function DateRangePicker({ from, to, preset, onChange }) {
  const c = useStyles();
  const [anchor, setAnchor] = useState(null);
  const [draft, setDraft] = useState({ from, to, preset });
  const [editing, setEditing] = useState("from");

  const open = (e) => {
    setDraft({ from, to, preset });
    setEditing("from");
    setAnchor(e.currentTarget);
  };
  const pickPreset = (key) => {
    const r = presetRange(key);
    setDraft({ from: r.from, to: r.to, preset: key });
  };
  const pickDay = (d) => {
    if (!d) return;
    const s = toStr(d);
    if (editing === "from") {
      setDraft((p) => ({ from: s, to: p.to && p.to < s ? s : p.to, preset: "custom" }));
      setEditing("to");
    } else {
      setDraft((p) => ({ from: p.from && p.from > s ? s : p.from, to: s, preset: "custom" }));
    }
  };
  const apply = () => {
    onChange(draft.preset, draft.from, draft.to);
    setAnchor(null);
  };
  const presetLabel = (DATE_PRESETS.find((p) => p.key === preset) || {}).label;
  const label =
    !from && !to
      ? "All time"
      : preset === "custom" || !presetLabel
      ? `${nice(from)} – ${nice(to)}`
      : `${presetLabel} · ${nice(from)}${from === to ? "" : ` – ${nice(to)}`}`;

  return (
    <>
      <div className={c.trigger} onClick={open} role="button" tabIndex={0}>
        <EventIcon style={{ fontSize: 18, color: "#f97316" }} />
        <span>{label}</span>
        <ArrowDropDownIcon style={{ color: "#94a3b8" }} />
      </div>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ className: c.paper }}
      >
        <div className={c.presets}>
          {DATE_PRESETS.filter((p) => p.key !== "custom").map((p) => (
            <span
              key={p.key}
              className={`${c.chip} ${draft.preset === p.key ? c.chipOn : ""}`}
              onClick={() => pickPreset(p.key)}
            >
              {p.label}
            </span>
          ))}
        </div>
        <div className={c.seg}>
          {[
            ["from", "From", draft.from],
            ["to", "To", draft.to],
          ].map(([k, l, v]) => (
            <div key={k} className={`${c.segBtn} ${editing === k ? c.segOn : ""}`} onClick={() => setEditing(k)}>
              <div className={c.segLabel}>{l}</div>
              <div className={c.segVal}>{nice(v)}</div>
            </div>
          ))}
        </div>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker
            variant="static"
            disableToolbar
            value={toDate(editing === "from" ? draft.from : draft.to) || new Date()}
            minDate={editing === "to" && draft.from ? toDate(draft.from) : undefined}
            onChange={pickDay}
          />
        </MuiPickersUtilsProvider>
        <div className={c.footer}>
          <Button size="small" onClick={() => setAnchor(null)} style={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button size="small" variant="contained" color="primary" disableElevation onClick={apply} style={{ textTransform: "none" }}>
            Apply
          </Button>
        </div>
      </Popover>
    </>
  );
}
